import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
import { DatabaseOperation, DatabaseLibrary, DatabaseQuerySchema } from './executionPathTracer.js';
import { HttpMethod } from './apiRoutes.js';
import { ApiCallSchema, TypedSchemaFieldSchema } from './apiCallTracer.js';
// ============= E2E Flow Template Types =============
/**
 * Flow step type classification
 */
export const FlowStepType = z.enum([
    'user-action', // User interaction (click, submit, navigate)
    'ui-component', // UI component rendering/state
    'pre-action-gate', // Confirmation/permission gate before API call (#898 F5)
    'api-call', // Frontend API call
    'middleware', // Backend middleware/auth gate before route handler (#898 F3)
    'api-route', // Backend route handler
    'backend-logic', // Backend processing logic (validation, transformation, service calls) (#390)
    'service-call', // Internal service call
    'database', // Database operation
    'external-api', // External API call
    'state-change', // State mutation
    'navigation', // Route navigation
    'navigation-target', // Navigation destination screen (#345)
    'modal-container', // Modal/dialog container (#348)
    'modal-outcome', // Modal interaction outcome (#348)
    'prop-callback', // Cross-component prop callback (#357)
    'api-response', // API response from backend (#369)
    'api-callback', // Frontend callback handling response (#369)
    'dom-update', // Final DOM change (#369)
    'error', // Error handling
    'cache', // Cache operation
]);
/**
 * Flow trigger type
 */
export const FlowTriggerType = z.enum([
    'click', // Button/element click
    'submit', // Form submission
    'navigate', // Route navigation
    'load', // Page/component load
    'event', // Custom event
    'timer', // Timer/interval
    'websocket', // WebSocket message
    'fetch', // Data fetching (useQuery, useSWR, fetch)
    'gesture', // Drag/drop, scroll, touch, pointer
    'realtime', // Real-time events (SSE, polling)
    'file', // File upload/download operations
]);
/**
 * Interaction category for behavior classification (#344)
 * Categorizes interactions by their behavior pattern
 * Issue #452: Added canvas-interaction, keyboard-shortcut, undo-redo, context-menu
 * Issue #453: Added file-operation, browser-dialog, analytics-tracking, tooltip-popover,
 *             tab-navigation, infinite-scroll, slider-carousel
 */
export const InteractionCategory = z.enum([
    'navigation', // Client-side navigation to different screen
    'toggle', // Boolean state toggle (show/hide, expand/collapse)
    'accordion-toggle', // ID-based single-selection toggle (#357)
    'local-state', // Local state update (filter, selection, form input)
    'modal-dialog', // Modal/dialog open/close/action
    'export-download', // File export or download
    'clipboard', // Copy to clipboard
    'form-submit', // Form submission (usually API-bound)
    'api-bound', // Direct API call interaction
    'canvas-interaction', // (#452) Drawing/pointer interactions on canvas elements
    'keyboard-shortcut', // (#452) Keyboard shortcuts and hotkeys
    'undo-redo', // (#452) History manipulation (undo/redo actions)
    'context-menu', // (#452) Right-click context menu actions
    // Issue #453: New categories
    'file-operation', // (#453) File upload, load, save, import operations
    'browser-dialog', // (#453) Native browser dialogs (alert, confirm, prompt)
    'analytics-tracking', // (#453) Analytics and tracking event handlers
    'tooltip-popover', // (#453) Hover-triggered tooltips and popovers
    'tab-navigation', // (#453) Tab switching and step wizards
    'infinite-scroll', // (#453) Lazy loading and infinite scroll
    'slider-carousel', // (#453) Carousel/slider navigation
    'unknown', // Unable to categorize
]);
/**
 * Modal outcome type for modal/dialog interactions (#348)
 * Describes what happens after a modal interaction
 */
export const ModalOutcomeType = z.enum([
    'dismiss', // Modal closes, no side effects
    'dismiss-reset', // Modal closes, form state reset
    'action-dismiss', // API call/action, then closes
    'stay-open', // Validation error, modal stays
]);
/**
 * Modal interaction info for container display (#348)
 */
export const ModalInteractionSchema = z.object({
    /** Trigger element name */
    element: z.string(),
    /** Handler function name */
    handler: z.string(),
    /** Source line number */
    line: z.number(),
    /** Type of outcome */
    outcomeType: ModalOutcomeType,
    /** User-friendly description */
    description: z.string(),
    /** State changes if any */
    stateChanges: z.array(z.string()).optional(),
    /** Associated API call if any */
    hasApiCall: z.boolean().optional(),
});
/**
 * Export format type for export/download interactions (#349)
 */
export const ExportFormat = z.enum([
    'image-raster', // PNG, JPG, WEBP
    'image-vector', // SVG
    'data', // JSON, CSV
    'code', // Mermaid, plaintext
    'document', // PDF
]);
/**
 * Export end state info (#349)
 */
export const ExportEndStateSchema = z.object({
    /** Icon for the format */
    icon: z.string(),
    /** File extension */
    fileExtension: z.string(),
    /** Format name */
    formatName: z.string(),
    /** Content description */
    contentDescription: z.string(),
    /** User-friendly effect */
    userEffect: z.string(),
});
// ============= Enhanced Step Data Schemas (#390) =============
/**
 * Enhanced database step data with detailed field information (#390)
 * Provides comprehensive view of database operations in E2E flows
 */
export const DatabaseStepDataSchema = z.object({
    /** Database operation type (select, insert, update, delete, etc.) */
    operation: DatabaseOperation,
    /** Table or collection name */
    table: z.string(),
    /** Model name if using ORM */
    model: z.string().optional(),
    /** ORM/database library used */
    library: DatabaseLibrary.optional(),
    /** Fields being selected or modified */
    fields: z.array(z.string()).optional(),
    /** Fields used in WHERE/filter conditions */
    whereFields: z.array(z.string()).optional(),
    /** Join information for related tables */
    joins: z.array(z.object({
        table: z.string(),
        type: z.string(),
        onField: z.string().optional(),
    })).optional(),
    /** Whether operation is part of a transaction */
    inTransaction: z.boolean().optional(),
    /** Raw query string if available (for SQL) */
    rawQuery: z.string().optional(),
    /** Estimated row count affected (if determinable) */
    estimatedRows: z.string().optional(),
});
/**
 * Backend logic step data for intermediate processing (#390)
 * Represents validation, transformation, and service calls between route and database
 */
export const BackendLogicStepDataSchema = z.object({
    /** Function or method name performing the logic */
    functionName: z.string(),
    /** Type of logic operation */
    logicType: z.enum([
        'validation', // Input validation
        'transformation', // Data transformation
        'authorization', // Auth checks
        'business-logic', // Business rules
        'aggregation', // Data aggregation
        'formatting', // Response formatting
        'other',
    ]),
    /** High-level description of operations performed */
    operations: z.array(z.string()),
    /** Input fields/parameters processed */
    inputFields: z.array(z.string()).optional(),
    /** Output fields/data produced */
    outputFields: z.array(z.string()).optional(),
    /** Called functions/services */
    calledFunctions: z.array(z.string()).optional(),
    /** Whether this is an async operation */
    isAsync: z.boolean().optional(),
});
/**
 * Enhanced API route step data with request/response details (#390)
 * Provides comprehensive view of API endpoint in E2E flows
 */
export const ApiRouteStepDataSchema = z.object({
    /** HTTP method(s) */
    method: z.union([HttpMethod, z.array(HttpMethod)]),
    /** Route path pattern */
    path: z.string(),
    /** Handler function name */
    handlerName: z.string().optional(),
    /** Framework used (express, fastify, etc.) */
    framework: z.string().optional(),
    /** Middleware applied to route */
    middleware: z.array(z.string()).optional(),
    /** Expected request body fields */
    requestFields: z.array(z.string()).optional(),
    /** Request body schema name (if typed) */
    requestSchemaName: z.string().optional(),
    /** Response fields returned */
    responseFields: z.array(z.string()).optional(),
    /** Response schema name (if typed) */
    responseSchemaName: z.string().optional(),
    /** Path parameters */
    pathParams: z.array(z.string()).optional(),
    /** Query parameters */
    queryParams: z.array(z.string()).optional(),
    /** Authentication required */
    requiresAuth: z.boolean().optional(),
});
/**
 * Enhanced API call step data with request details (#390)
 */
export const ApiCallStepDataSchema = z.object({
    /** HTTP method */
    method: HttpMethod,
    /** API endpoint */
    endpoint: z.string(),
    /** Whether endpoint is dynamic (contains variables) */
    isDynamic: z.boolean().optional(),
    /** Client library used */
    library: z.enum(['fetch', 'axios', 'ky', 'swr', 'react-query', 'custom']).optional(),
    /** Request body fields sent */
    requestFields: z.array(z.string()).optional(),
    /** Path parameters */
    pathParams: z.array(z.string()).optional(),
    /** Query parameters */
    queryParams: z.array(z.string()).optional(),
    /** Wrapper function name (e.g., api.startAnalysis) */
    wrapperFunction: z.string().optional(),
});
// ============= Flow Step Schema =============
/**
 * Individual step in an E2E flow
 */
export const E2EFlowStepSchema = z.object({
    /** Unique step identifier */
    id: z.string(),
    /** Step type */
    type: FlowStepType,
    /** Display name */
    name: z.string(),
    /** UI label for display */
    label: z.string(),
    /** Description */
    description: z.string().optional(),
    /** Additional step data */
    data: z.record(z.unknown()),
    /** Source file path */
    sourceFile: z.string().optional(),
    /** Source line number */
    sourceLine: z.number().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
    /** Duration (ms) if available */
    duration: z.number().optional(),
    /** Whether step is async */
    isAsync: z.boolean().optional(),
    /** Error info if step represents error */
    error: z.object({
        type: z.string(),
        message: z.string(),
    }).optional(),
});
// ============= Flow Path Schema =============
/**
 * Complete E2E flow path from trigger to completion
 */
export const E2EFlowPathSchema = z.object({
    /** Unique flow identifier */
    id: z.string(),
    /** Flow name */
    name: z.string(),
    /** Flow description */
    description: z.string().optional(),
    /** Trigger type */
    triggerType: FlowTriggerType,
    /** Trigger name (element/event name) */
    triggerName: z.string(),
    /** Handler function name (e.g., handleConfirm, onClose) - Issue #378 */
    handlerName: z.string().optional(),
    /** Element text content (button label, link text, etc.) - Issue #378 */
    elementText: z.string().optional(),
    /** Interaction behavior category (#344) */
    interactionCategory: InteractionCategory.optional(),
    /** Human-readable unique interaction identifier (#422) */
    interactionIID: z.string().optional(),
    /** Screen name where interaction occurs (#422) */
    screen: z.string().optional(),
    /** Steps in the flow */
    steps: z.array(E2EFlowStepSchema),
    /** Whether flow has all expected connections */
    complete: z.boolean(),
    /** External services touched */
    touchedServices: z.array(z.string()),
    /** Database tables touched */
    touchedTables: z.array(z.string()),
    /** API calls made in this flow (Issue #419) */
    apiCalls: z.array(ApiCallSchema).optional(),
    /** Database queries made in this flow (Issue #419) */
    databaseQueries: z.array(DatabaseQuerySchema).optional(),
    /** Tags for categorization */
    tags: z.array(z.string()).optional(),
    /** Priority/importance */
    priority: z.enum(['critical', 'high', 'medium', 'low']).optional(),
    /** Whether this flow is an internal/system interaction (not user-visible) */
    isInternal: z.boolean().optional(),
    /** Original UserInteraction.id — preserved for deep linking from artifact pages (Issue #647) */
    sourceInteractionId: z.string().optional(),
    /** Confidence level: deterministic (AST-proven) or heuristic (pattern-matched) */
    confidence: z.enum(['deterministic', 'heuristic']).optional(),
    /** Reason for heuristic confidence classification */
    confidenceReason: z.string().optional(),
});
// ============= Flow Summary Schema =============
/**
 * Summary statistics for E2E flows
 */
export const E2EFlowSummarySchema = z.object({
    /** Total number of flows */
    totalFlows: z.number(),
    /** Total steps across all flows */
    totalSteps: z.number(),
    /** Average steps per flow */
    avgStepsPerFlow: z.number(),
    /** Number of user actions */
    userActionsCount: z.number(),
    /** Number of API calls */
    apiCallsCount: z.number(),
    /** Number of database queries */
    databaseQueriesCount: z.number(),
    /** Number of external API calls */
    externalApiCallsCount: z.number(),
    /** Number of complete flows */
    completeFlowsCount: z.number().optional(),
    /** Number of incomplete flows */
    incompleteFlowsCount: z.number().optional(),
});
// ============= Flow Diagram Data Schema =============
/**
 * Complete E2E flow diagram data
 */
export const E2EFlowDiagramDataSchema = z.object({
    /** All detected flows */
    flows: z.array(E2EFlowPathSchema),
    /** Diagram graph (nodes and edges) */
    graph: z.object({
        nodes: z.array(z.object({
            id: z.string(),
            type: z.string(),
            label: z.string(),
            description: z.string().optional(),
            position: z.object({
                x: z.number(),
                y: z.number(),
            }),
            data: z.record(z.unknown()).optional(),
            evidence: z.array(CodeEvidenceSchema).optional(),
        })),
        edges: z.array(z.object({
            id: z.string(),
            source: z.string(),
            target: z.string(),
            label: z.string().optional(),
            type: z.enum(['default', 'http', 'async', 'database', 'file']).optional(),
            animated: z.boolean().optional(),
            data: z.record(z.unknown()).optional(),
        })),
    }),
    /** Summary statistics */
    summary: E2EFlowSummarySchema,
});
// ============= Template Configuration =============
/**
 * Visual style for flow step
 */
export const FlowStepStyleSchema = z.object({
    /** Background color */
    backgroundColor: z.string(),
    /** Border color */
    borderColor: z.string(),
    /** Icon name */
    icon: z.string(),
    /** Icon color */
    iconColor: z.string().optional(),
    /** Text color */
    textColor: z.string().optional(),
    /** Shape type */
    shape: z.enum(['rectangle', 'rounded', 'circle', 'diamond', 'hexagon']).optional(),
});
/**
 * Template configuration for flow rendering
 */
export const E2EFlowTemplateConfigSchema = z.object({
    /** Template name */
    name: z.string(),
    /** Template description */
    description: z.string().optional(),
    /** Step type styles */
    stepStyles: z.record(FlowStepStyleSchema),
    /** Edge styles by type */
    edgeStyles: z.record(z.object({
        strokeColor: z.string(),
        strokeWidth: z.number().optional(),
        strokeDasharray: z.string().optional(),
        animated: z.boolean().optional(),
    })).optional(),
    /** Layout options */
    layout: z.object({
        /** Layout direction */
        direction: z.enum(['horizontal', 'vertical', 'radial']),
        /** Node spacing */
        nodeSpacing: z.number().optional(),
        /** Level spacing */
        levelSpacing: z.number().optional(),
        /** Whether to group by type */
        groupByType: z.boolean().optional(),
    }).optional(),
    /** Interaction options */
    interactions: z.object({
        /** Enable click to view source */
        clickToSource: z.boolean().optional(),
        /** Enable hover details */
        hoverDetails: z.boolean().optional(),
        /** Enable flow highlighting */
        highlightFlow: z.boolean().optional(),
        /** Enable step filtering */
        filterByType: z.boolean().optional(),
    }).optional(),
});
// ============= Flow Filter Options =============
/**
 * Filter options for E2E flow view
 */
export const E2EFlowFilterOptionsSchema = z.object({
    /** Filter by trigger type */
    triggerTypes: z.array(FlowTriggerType).optional(),
    /** Filter by step types to include */
    includeStepTypes: z.array(FlowStepType).optional(),
    /** Filter by step types to exclude */
    excludeStepTypes: z.array(FlowStepType).optional(),
    /** Only show complete flows */
    completeOnly: z.boolean().optional(),
    /** Only show incomplete flows */
    incompleteOnly: z.boolean().optional(),
    /** Filter by touched services */
    services: z.array(z.string()).optional(),
    /** Filter by touched tables */
    tables: z.array(z.string()).optional(),
    /** Search query */
    searchQuery: z.string().optional(),
    /** Minimum steps in flow */
    minSteps: z.number().optional(),
    /** Maximum steps in flow */
    maxSteps: z.number().optional(),
});
// ============= Flow View State =============
/**
 * UI state for E2E flow viewer
 */
export const E2EFlowViewStateSchema = z.object({
    /** Currently selected flow ID */
    selectedFlowId: z.string().nullable(),
    /** Currently highlighted step ID */
    highlightedStepId: z.string().nullable(),
    /** Expanded flow IDs (for collapsible list) */
    expandedFlowIds: z.array(z.string()),
    /** Active filter options */
    filterOptions: E2EFlowFilterOptionsSchema,
    /** Active template config */
    templateConfig: E2EFlowTemplateConfigSchema.optional(),
    /** Zoom level */
    zoom: z.number(),
    /** Pan position */
    pan: z.object({
        x: z.number(),
        y: z.number(),
    }),
    /** View mode */
    viewMode: z.enum(['diagram', 'list', 'timeline', 'catalog']),
});
// ============= Edge Info Types (Issue #434) =============
/**
 * Edge type classification for HTTP communication edges
 */
export const EdgeInfoType = z.enum([
    'request', // API call → API route (HTTP request)
    'response', // API route → API response (HTTP response)
    'callback', // API response → callback handler
    'database', // Backend → database operation
    'other', // Other edge types
]);
/**
 * Request information for HTTP request edges
 */
export const EdgeRequestInfoSchema = z.object({
    /** HTTP method */
    method: z.string(),
    /** API endpoint path */
    endpoint: z.string(),
    /** Path parameters */
    pathParams: z.array(z.string()).optional(),
    /** Query parameters */
    queryParams: z.array(z.string()).optional(),
    /** Request body field names */
    bodyFields: z.array(z.string()).optional(),
    /** Request body schema name if typed */
    bodySchemaName: z.string().optional(),
    /** Issue #435: Request body typed fields with type information */
    bodyTypedFields: z.array(TypedSchemaFieldSchema).optional(),
    /** Content type */
    contentType: z.string().optional(),
    /** Headers (detection flags) */
    headers: z.record(z.string()).optional(),
});
/**
 * Response information for HTTP response edges
 */
export const EdgeResponseInfoSchema = z.object({
    /** HTTP status code */
    statusCode: z.number(),
    /** Response type description */
    type: z.string(),
    /** Response body field names */
    bodyFields: z.array(z.string()).optional(),
    /** Response body schema name if typed */
    bodySchemaName: z.string().optional(),
    /** Issue #435: Response body typed fields with type information */
    bodyTypedFields: z.array(TypedSchemaFieldSchema).optional(),
    /** Content type */
    contentType: z.string().optional(),
});
/**
 * Issue #438: Contextual evidence with labels for edge detail panel
 * Separates source and target evidence with descriptive labels
 */
export const EdgeContextualEvidenceSchema = z.object({
    /** Label describing the context (e.g., "Calling Code", "Handler Code") */
    label: z.string(),
    /** Code evidence for this context */
    evidence: z.array(CodeEvidenceSchema),
});
/**
 * Issue #438: Database operation details for edge detail panel
 * Shows query details when edge involves database node
 */
export const EdgeDatabaseInfoSchema = z.object({
    /** Database operation type (select, insert, update, delete, etc.) */
    operation: z.string().optional(),
    /** Table or collection name */
    table: z.string().optional(),
    /** Model name if using ORM */
    model: z.string().optional(),
    /** Fields being selected or modified */
    fields: z.array(z.string()).optional(),
    /** Fields used in WHERE/filter conditions */
    whereFields: z.array(z.string()).optional(),
    /** Whether this is a query (to database) or result (from database) direction */
    direction: z.enum(['query', 'result']).optional(),
});
/**
 * Node reference for edge source/target
 */
export const EdgeNodeRefSchema = z.object({
    /** Node ID */
    id: z.string(),
    /** Node type (step type) */
    type: z.string(),
    /** Node display label */
    label: z.string(),
});
/**
 * Complete edge information for edge detail panel (Issue #434)
 * Combines data from source and target nodes to display HTTP communication details
 * Issue #438: Enhanced with contextual evidence and database info
 */
export const EdgeInfoSchema = z.object({
    /** Edge ID */
    id: z.string(),
    /** Edge type classification */
    type: EdgeInfoType,
    /** Request information (for request edges) */
    request: EdgeRequestInfoSchema.optional(),
    /** Response information (for response edges) */
    response: EdgeResponseInfoSchema.optional(),
    /** Source node reference */
    sourceNode: EdgeNodeRefSchema,
    /** Target node reference */
    targetNode: EdgeNodeRefSchema,
    /** Combined code evidence from both nodes (kept for backward compatibility) */
    evidence: z.array(CodeEvidenceSchema).optional(),
    /** Issue #438: Source node evidence with contextual label */
    sourceEvidence: EdgeContextualEvidenceSchema.optional(),
    /** Issue #438: Target node evidence with contextual label */
    targetEvidence: EdgeContextualEvidenceSchema.optional(),
    /** Issue #438: Database operation details (for database edges) */
    database: EdgeDatabaseInfoSchema.optional(),
});
//# sourceMappingURL=e2eFlowTemplate.js.map